import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  MapPin,
  Phone,
  Clock,
  Star,
  Calendar,
  Award,
  Stethoscope,
  User,
  ChevronLeft,
  Loader2,
  CheckCircle,
  Video,
  Home,
  Building2
} from 'lucide-react';

const PublicProfessionalProfile = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    loadProfile();
  }, [slug]);

  const loadProfile = async () => {
    try {
      const response = await api.get(`/seo/pro/${slug}`);
      setData(response.data);
    } catch (err) {
      console.error('Error loading profile:', err);
      setError('Professionnel non trouvé');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1976D2]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-slate-900 mb-2">Professionnel non trouvé</h1>
            <p className="text-slate-500 mb-6">Ce profil n'existe pas ou n'est plus disponible.</p>
            <Button onClick={() => navigate('/rechercher')} className="bg-[#1976D2]">
              Rechercher un professionnel
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { profile, user, reviews, stats, seo } = data;

  // Structured data for Google (JSON-LD)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Physician",
    "name": user?.full_name || "Médecin",
    "medicalSpecialty": profile?.specialty,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": profile?.address,
      "addressLocality": profile?.city,
      "addressCountry": "CM"
    },
    "telephone": profile?.phone,
    "priceRange": `${profile?.consultation_fee} FCFA`,
    "aggregateRating": stats?.reviews_count > 0 ? {
      "@type": "AggregateRating",
      "ratingValue": stats?.average_rating,
      "reviewCount": stats?.reviews_count
    } : undefined
  };

  return (
    <>
      <Helmet>
        <title>{seo?.title}</title>
        <meta name="description" content={seo?.description} />
        <link rel="canonical" href={`https://dockhta.com${seo?.canonical_url}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={seo?.title} />
        <meta property="og:description" content={seo?.description} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={`https://dockhta.com${seo?.canonical_url}`} />
        <meta property="og:image" content={user?.profile_image || 'https://dockhta.com/og-image.png'} />
        <meta property="og:site_name" content="DOCKHTA" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo?.title} />
        <meta name="twitter:description" content={seo?.description} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo192.png" alt="DOCKHTA" className="w-8 h-8" />
              <span className="font-bold text-[#1976D2]">DOCKHTA</span>
            </Link>
            <Button variant="outline" onClick={() => navigate(-1)} size="sm">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Retour
            </Button>
          </div>
        </header>

        <main className="max-w-4xl mx-auto p-4 pb-24">
          {/* Profile Header */}
          <Card className="mb-6 border-0 shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-[#1976D2] to-[#1565C0] p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 bg-white rounded-xl flex items-center justify-center flex-shrink-0">
                  {user?.profile_image ? (
                    <img src={user.profile_image} alt={user.full_name} className="w-full h-full object-cover rounded-xl" />
                  ) : (
                    <Stethoscope className="w-10 h-10 text-[#1976D2]" />
                  )}
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl font-bold">
                    {user?.full_name?.startsWith('Dr.') || user?.full_name?.startsWith('Dr ') 
                      ? user.full_name 
                      : `Dr. ${user?.full_name}`}
                  </h1>
                  <p className="text-blue-100 text-lg">{profile?.specialty}</p>
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    {profile?.is_validated && (
                      <Badge className="bg-white/20 text-white">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Vérifié
                      </Badge>
                    )}
                    {profile?.years_experience && (
                      <Badge className="bg-white/20 text-white">
                        <Award className="w-3 h-3 mr-1" />
                        {profile.years_experience} ans d'expérience
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <CardContent className="p-5">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-5 text-center">
                <div className="bg-amber-50 rounded-xl p-3">
                  <div className="flex items-center justify-center gap-1 text-amber-600">
                    <Star className="w-5 h-5 fill-amber-400" />
                    <span className="font-bold text-xl">{stats?.average_rating || '-'}</span>
                  </div>
                  <p className="text-xs text-slate-500">{stats?.reviews_count || 0} avis</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-3">
                  <p className="font-bold text-xl text-blue-600">{stats?.appointments_count || 0}</p>
                  <p className="text-xs text-slate-500">Consultations</p>
                </div>
                <div className="bg-green-50 rounded-xl p-3">
                  <p className="font-bold text-xl text-green-600">{profile?.consultation_fee?.toLocaleString() || '-'}</p>
                  <p className="text-xs text-slate-500">FCFA</p>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-3">
                {profile?.address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{profile.address}</p>
                      <p className="text-sm text-slate-500">{profile.city}{profile.neighborhood ? `, ${profile.neighborhood}` : ''}</p>
                    </div>
                  </div>
                )}
                {profile?.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-slate-400" />
                    <a href={`tel:${profile.phone}`} className="font-medium text-[#1976D2]">{profile.phone}</a>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* About */}
          {profile?.bio && (
            <Card className="mb-6 border-0 shadow-md">
              <CardContent className="p-5">
                <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <User className="w-5 h-5 text-[#1976D2]" />
                  À propos
                </h2>
                <p className="text-slate-600 leading-relaxed">{profile.bio}</p>
              </CardContent>
            </Card>
          )}

          {/* Services & Pricing */}
          <Card className="mb-6 border-0 shadow-md">
            <CardContent className="p-5">
              <h2 className="font-semibold text-slate-900 mb-4">Tarifs</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <Building2 className="w-6 h-6 text-[#1976D2] mx-auto mb-2" />
                  <p className="text-xs text-slate-500">Consultation</p>
                  <p className="font-bold text-lg text-slate-900">{profile?.consultation_fee?.toLocaleString() || '-'} FCFA</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <Home className="w-6 h-6 text-green-600 mx-auto mb-2" />
                  <p className="text-xs text-slate-500">À domicile</p>
                  <p className="font-bold text-lg text-slate-900">{profile?.home_visit_fee?.toLocaleString() || '-'} FCFA</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <Video className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                  <p className="text-xs text-slate-500">Téléconsultation</p>
                  <p className="font-bold text-lg text-slate-900">{profile?.video_fee?.toLocaleString() || '-'} FCFA</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Services */}
          {profile?.services_offered?.length > 0 && (
            <Card className="mb-6 border-0 shadow-md">
              <CardContent className="p-5">
                <h2 className="font-semibold text-slate-900 mb-3">Services proposés</h2>
                <div className="flex flex-wrap gap-2">
                  {profile.services_offered.map((service, i) => (
                    <Badge key={i} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      {service}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Reviews */}
          {reviews?.length > 0 && (
            <Card className="mb-6 border-0 shadow-md">
              <CardContent className="p-5">
                <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500" />
                  Avis patients ({reviews.length})
                </h2>
                <div className="space-y-4">
                  {reviews.slice(0, 5).map((review, i) => (
                    <div key={i} className="border-b last:border-0 pb-4 last:pb-0">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${star <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`}
                            />
                          ))}
                        </div>
                        <span className="text-sm text-slate-500">
                          {new Date(review.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                      {review.comment && <p className="text-slate-600 text-sm">{review.comment}</p>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* CTA */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 shadow-lg">
            <div className="max-w-4xl mx-auto">
              <Button
                onClick={() => navigate(`/rechercher?professional=${profile?.user_id}`)}
                className="w-full bg-[#E53935] hover:bg-[#C62828] h-12 rounded-xl text-base font-medium"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Prendre rendez-vous
              </Button>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default PublicProfessionalProfile;
